import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Users } from 'lucide-react';
import api from '../../api';
import { createGeneralSchedule, updateGeneralSchedule } from './api/generalScheduleApi';

// ─── Constants ───────────────────────────────────────────────────────────────
const GENERAL_SCHEDULE_TYPES = [
  { id: 'meeting', label: 'Meeting' },
  { id: 'sales-visit', label: 'Sales Visit' },
  { id: 'business-trip', label: 'Business Trip / Travel' },
  { id: 'event', label: 'Event' },
];

const SCHEDULE_TYPE_LABEL = {
  meeting: 'Meeting',
  'sales-visit': 'Sales Visit',
  'business-trip': 'Business Trip / Travel',
  event: 'Event',
};

// ─── Component ───────────────────────────────────────────────────────────────
const AddGeneralScheduleOffcanvas = ({
  isOpen,
  scheduleType,
  initialDate,
  editData,
  onScheduleSaved,
  onClose,
  onAuditTypeSelected,
  /** When false, hide the control that jumps to Audit Scheduling (separate permission). */
  allowAuditTypeSwitch = true,
}) => {
  // Form state
  const [selectedType, setSelectedType] = useState(scheduleType || 'meeting');
  const [title, setTitle] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [location, setLocation] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState([]);
  const [userSearch, setUserSearch] = useState('');

  // Data
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [companySites, setCompanySites] = useState([]);

  // Status
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState(null);
  const notificationTimerRef = useRef(null);

  const showNotification = useCallback((type, message) => {
    if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current);
    setNotification({ type, message });
    notificationTimerRef.current = setTimeout(() => setNotification(null), 5000);
  }, []);

  useEffect(() => () => { if (notificationTimerRef.current) clearTimeout(notificationTimerRef.current); }, []);

  const resetForm = useCallback(() => {
    setTitle('');
    setStartDate('');
    setEndDate('');
    setNotes('');
    setLocation('');
    setSelectedCompanyId('');
    setSelectedAssigneeIds([]);
    setUserSearch('');
    setNotification(null);
  }, []);

  // ── Load users on mount ───────────────────────────────────────────────────
  useEffect(() => {
    const loadUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const res = await api.get('/md/internal-users/option-internal-user');
        setUsers(Array.isArray(res?.data) ? res.data : []);
      } catch (err) {
        console.warn('Failed to load internal users:', err);
        setUsers([]);
      } finally {
        setIsLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  // ── Load company master options on mount ──────────────────────────────────
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const res = await api.get('/md/companies');
        const rows = Array.isArray(res?.data) ? res.data : [];
        setCompanies(rows
          .map((company) => ({
            id: company?.id,
            name: company?.companyName || company?.name || '',
          }))
          .filter((company) => company.id && company.name)
        );
      } catch (err) {
        console.warn('Failed to load companies:', err);
        setCompanies([]);
      }
    };
    loadCompanies();
  }, []);

  // Company sites belong to projects, so load them only after a company is selected.
  // This keeps the dropdown accurate without downloading every project's sites on panel open.
  useEffect(() => {
    let isActive = true;

    const loadCompanySites = async () => {
      if (!selectedCompanyId) {
        setCompanySites([]);
        return;
      }

      setIsLoadingSites(true);
      try {
        const projectsResponse = await api.get(`/crm/projects/company/${selectedCompanyId}`);
        const projects = Array.isArray(projectsResponse?.data) ? projectsResponse.data : [];
        const siteResults = await Promise.all(
          projects
            .map((project) => project?.id)
            .filter(Boolean)
            .map((projectId) => api.get(`/crm/projects/${projectId}/sites`).catch(() => ({ data: [] })))
        );
        if (!isActive) return;

        const seen = new Set();
        const sites = siteResults.flatMap((response) => Array.isArray(response?.data) ? response.data : [])
          .map((site) => ({
            id: site?.id,
            name: site?.siteName || site?.name || site?.address || '',
            address: site?.address || '',
          }))
          .filter((site) => site.name)
          .filter((site) => {
            const key = String(site.id || `${site.name}|${site.address}`).toLowerCase();
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        setCompanySites(sites);
      } catch (err) {
        if (!isActive) return;
        console.warn('Failed to load company project sites:', err);
        setCompanySites([]);
      } finally {
        if (isActive) setIsLoadingSites(false);
      }
    };

    loadCompanySites();
    return () => { isActive = false; };
  }, [selectedCompanyId]);

  // ── Open/close the Bootstrap offcanvas via isOpen prop ────────────────────
  useEffect(() => {
    const el = document.getElementById('offcanvasGeneralSchedule');
    if (!el || !window.bootstrap?.Offcanvas) return;
    let instance = window.bootstrap.Offcanvas.getInstance(el);
    if (!instance) instance = new window.bootstrap.Offcanvas(el);
    if (isOpen) {
      instance.show();
    } else {
      instance.hide();
    }
  }, [isOpen]);

  // ── Sync type and date when panel opens (edit or create mode) ────────────
  useEffect(() => {
    if (isOpen) {
      if (editData) {
        // EDIT mode - pre-fill from existing schedule
        setSelectedType(editData.type || scheduleType || 'meeting');
        setTitle(editData.title || '');
        setStartDate(String(editData.startDate || '').slice(0, 10));
        setEndDate(String(editData.endDate || editData.startDate || '').slice(0, 10));
        setNotes(editData.notes || '');
        setLocation(editData.location || '');
        setSelectedCompanyId(editData.companyId ? String(editData.companyId) : '');
        setSelectedAssigneeIds(
          Array.isArray(editData.assigneeIds)
            ? editData.assigneeIds.map(Number).filter(Number.isFinite).map(String)
            : []
        );
      } else {
        // CREATE mode - reset form
        setSelectedType(scheduleType || 'meeting');
        setTitle('');
        setStartDate(initialDate || '');
        setEndDate('');
        setNotes('');
        setLocation('');
        setSelectedCompanyId('');
        setSelectedAssigneeIds([]);
        setCompanySites([]);
      }
    }
  // eslint-disable-next-line
  }, [isOpen, editData, scheduleType, initialDate]);

  // ── Bootstrap hidden event → reset + notify parent ────────────────────────
  useEffect(() => {
    const el = document.getElementById('offcanvasGeneralSchedule');
    if (!el) return;
    const handleHidden = () => {
      resetForm();
      if (typeof onClose === 'function') onClose();
    };
    el.addEventListener('hidden.bs.offcanvas', handleHidden);
    return () => el.removeEventListener('hidden.bs.offcanvas', handleHidden);
  }, [resetForm, onClose]);

  // ── Handle Audit type selection ───────────────────────────────────────────
  const handleSelectAuditType = () => {
    const el = document.getElementById('offcanvasGeneralSchedule');
    if (el && window.bootstrap?.Offcanvas) {
      const instance = window.bootstrap.Offcanvas.getInstance(el);
      if (instance) instance.hide();
    }
    if (typeof onAuditTypeSelected === 'function') onAuditTypeSelected();
  };

  // ── Assignee toggle ───────────────────────────────────────────────────────
  const toggleAssignee = (userId) => {
    const id = String(userId);
    setSelectedAssigneeIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllVisible = () => {
    const visibleIds = filteredUsers.map((u) => String(u.id || u.userId || ''));
    setSelectedAssigneeIds((prev) => Array.from(new Set([...prev, ...visibleIds])));
  };

  const clearAll = () => setSelectedAssigneeIds([]);

  // ── Save handler ──────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!title.trim()) {
      showNotification('warning', 'Please enter a schedule title.');
      return;
    }
    if (!startDate) {
      showNotification('warning', 'Please select a start date.');
      return;
    }
    if (selectedAssigneeIds.length === 0) {
      showNotification('warning', 'Please select at least one assignee.');
      return;
    }
    const parsedAssigneeIds = selectedAssigneeIds.map((id) => Number(id)).filter((n) => Number.isFinite(n));
    if (parsedAssigneeIds.length === 0) {
      showNotification('warning', 'Could not resolve valid assignee IDs. Please refresh and try again.');
      return;
    }

    setIsSaving(true);
    try {
      const typeLabel = SCHEDULE_TYPE_LABEL[selectedType] || selectedType;
      const descParts = [typeLabel];
      if (location) descParts.push(`Location: ${location}`);
      if (notes) descParts.push(notes);
      const description = descParts.join('\n');
      const dueDate = endDate || startDate;

      // Resolve companyId
      const selectedCompany = companies.find(
        (c) => String(c.id) === String(selectedCompanyId) || c.name === selectedCompanyId
      );
      const companyId = selectedCompany?.id ? parseInt(selectedCompany.id, 10) : null;
      const companyName = selectedCompany?.name || null;

      // 1. Save schedule to backend
      let savedScheduleId = null;
      try {
        const schedulePayload = {
          type: selectedType,
          title: title.trim(),
          startDate,
          endDate: endDate || null,
          notes,
          location,
          companyId: Number.isFinite(companyId) ? companyId : null,
          companyName,
          assigneeIds: parsedAssigneeIds,
        };
        let saved;
        if (editData) {
          saved = await updateGeneralSchedule(editData.id, schedulePayload);
        } else {
          saved = await createGeneralSchedule(schedulePayload);
        }
        savedScheduleId = saved?.id || null;
      } catch (schedErr) {
        console.error('Failed to save general schedule to backend:', schedErr);
        showNotification('danger', 'Failed to save schedule to server. Please try again.');
        return;
      }

      // 2. Create a task for each assignee
      const taskResults = await Promise.allSettled(
        selectedAssigneeIds.map(async (userId) => {
          const taskRequest = {
            title: title.trim(),
            type: typeLabel,
            priority: 'Medium',
            status: 'OPEN',
            description,
            dueDate: new Date(dueDate + 'T00:00:00').toISOString(),
            companyId: Number.isFinite(companyId) ? companyId : null,
            assignedUserId: Number(userId),
          };
          const formData = new FormData();
          formData.append(
            'data',
            new Blob([JSON.stringify(taskRequest)], { type: 'application/json' })
          );
          return api.post('/crm/lead-tasks', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        })
      );

      const successCount = taskResults.filter((r) => r.status === 'fulfilled').length;
      const failCount = taskResults.length - successCount;

      const msg =
        failCount > 0
          ? `Schedule saved! ${successCount} task${successCount !== 1 ? 's' : ''} created, ${failCount} failed.`
          : `Schedule saved! ${successCount} task${successCount !== 1 ? 's' : ''} assigned successfully.`;
      showNotification('success', msg);

      if (typeof onScheduleSaved === 'function') onScheduleSaved();

      setTimeout(() => {
        const el = document.getElementById('offcanvasGeneralSchedule');
        if (el && window.bootstrap?.Offcanvas) {
          const instance = window.bootstrap.Offcanvas.getInstance(el);
          if (instance) instance.hide();
        }
      }, 1300);
    } catch (err) {
      console.error('Failed to create general schedule:', err);
      showNotification('danger', 'Failed to create schedule. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // ── Derived ───────────────────────────────────────────────────────────────
  const filteredUsers = users.filter((u) => {
    if (!userSearch.trim()) return true;
    const q = userSearch.toLowerCase();
    return (
      String(u.fullName || u.label || u.name || '').toLowerCase().includes(q) ||
      String(u.email || '').toLowerCase().includes(q)
    );
  });

  const scheduleTypeLabel = SCHEDULE_TYPE_LABEL[selectedType] || selectedType;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      className="offcanvas offcanvas-end calendar-offcanvas-audit"
      tabIndex={-1}
      id="offcanvasGeneralSchedule"
      aria-labelledby="offcanvasGeneralScheduleLabel"
      style={{ width: '580px', maxWidth: '96vw' }}
    >
      <div className="offcanvas-header">
        <h2 className="offcanvas-title" id="offcanvasGeneralScheduleLabel">
          {editData ? `Edit ${scheduleTypeLabel}` : `Add ${scheduleTypeLabel}`}
        </h2>
        <button
          type="button"
          className="btn-close text-reset"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
        />
      </div>

      <div className="offcanvas-body d-flex flex-column gap-3" style={{ overflowY: 'auto' }}>
        {/* Notification banner */}
        {notification && (
          <div
            className={`alert alert-${
              notification.type === 'success'
                ? 'success'
                : notification.type === 'danger'
                ? 'danger'
                : 'warning'
            } py-2 mb-0`}
            role="alert"
          >
            {notification.message}
          </div>
        )}

        {/* ── Schedule type & basic info ── */}
        <div className="card p-3 mb-0">
          <div className="row g-3">
            {/* Type */}
            <div className="col-12">
              <label className="form-label fw-semibold">
                Schedule Type <span className="text-danger">*</span>
              </label>
              <div className="d-flex gap-2 flex-wrap">
                {GENERAL_SCHEDULE_TYPES.map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    className={`btn btn-sm ${
                      selectedType === type.id ? 'btn-primary' : 'btn-outline-secondary'
                    }`}
                    onClick={() => setSelectedType(type.id)}
                  >
                    {type.label}
                  </button>
                ))}
                {allowAuditTypeSwitch ? (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleSelectAuditType}
                  title="Open Audit Scheduling form"
                >
                  Audit
                </button>
                ) : null}
              </div>
            </div>

            {/* Title */}
            <div className="col-12">
              <label className="form-label fw-semibold">
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={`${scheduleTypeLabel} subject or agenda...`}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
            </div>

            {/* Dates */}
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                Start Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label fw-semibold">
                End Date{' '}
                <span className="text-muted fw-normal" style={{ fontSize: '0.8em' }}>
                  (optional)
                </span>
              </label>
              <input
                type="date"
                className="form-control"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* ── Assignees ── */}
        <div className="card p-3 mb-0">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <label className="form-label fw-semibold mb-0 d-flex align-items-center gap-1">
              <Users size={16} />
              Assignees <span className="text-danger">*</span>
            </label>
            <div className="d-flex gap-1">
              {filteredUsers.length > 0 && (
                <button
                  type="button"
                  className="btn btn-xs btn-outline-primary"
                  style={{ fontSize: '0.72rem', padding: '1px 8px' }}
                  onClick={selectAllVisible}
                >
                  Select all
                </button>
              )}
              {selectedAssigneeIds.length > 0 && (
                <button
                  type="button"
                  className="btn btn-xs btn-outline-secondary"
                  style={{ fontSize: '0.72rem', padding: '1px 8px' }}
                  onClick={clearAll}
                >
                  Clear
                </button>
              )}
            </div>
          </div>
          <input
            type="text"
            className="form-control form-control-sm mb-2"
            placeholder="Search staff by name or email..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
          />
          {isLoadingUsers ? (
            <div className="text-muted small py-2">
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              />
              Loading staff...
            </div>
          ) : (
            <div
              style={{
                maxHeight: '220px',
                overflowY: 'auto',
                border: '1px solid #dee2e6',
                borderRadius: '0.375rem',
              }}
            >
              {filteredUsers.length === 0 ? (
                <div className="text-muted small p-2">No staff found.</div>
              ) : (
                filteredUsers.map((u) => {
                  const uid = String(u.id || u.userId || '');
                  const name = u.fullName || u.label || u.name || `User #${uid}`;
                  const isChecked = selectedAssigneeIds.includes(uid);
                  return (
                    <label
                      key={uid}
                      className={`d-flex align-items-center gap-2 px-2 py-1 ${
                        isChecked ? 'bg-primary-lt' : ''
                      }`}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      <input
                        type="checkbox"
                        className="form-check-input m-0 flex-shrink-0"
                        checked={isChecked}
                        onChange={() => toggleAssignee(uid)}
                      />
                      <span className="flex-grow-1 small fw-medium">{name}</span>
                      {u.email && (
                        <span className="text-muted" style={{ fontSize: '0.72rem' }}>
                          {u.email}
                        </span>
                      )}
                    </label>
                  );
                })
              )}
            </div>
          )}
          {selectedAssigneeIds.length > 0 && (
            <div className="mt-1 text-muted small">
              {selectedAssigneeIds.length} staff member
              {selectedAssigneeIds.length !== 1 ? 's' : ''} selected — a task will be created
              for each
            </div>
          )}
        </div>

        {/* ── Optional fields ── */}
        <div className="card p-3 mb-0">
          <div className="row g-3">
            {/* Company */}
            <div className="col-12">
              <label className="form-label fw-semibold">
                Company{' '}
                <span className="text-muted fw-normal" style={{ fontSize: '0.8em' }}>
                  (optional)
                </span>
              </label>
              <select
                className="form-select"
                value={selectedCompanyId}
                onChange={(e) => {
                  setSelectedCompanyId(e.target.value);
                  setLocation('');
                }}
              >
                <option value="">No company</option>
                {companies.map((c) => (
                  <option key={c.name} value={c.id ?? c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Location / Site from the selected company's audit projects */}
            <div className="col-12">
              <label className="form-label fw-semibold">
                Location / Site{' '}
                <span className="text-muted fw-normal" style={{ fontSize: '0.8em' }}>
                  (optional)
                </span>
              </label>
              <select
                className="form-select"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                disabled={!selectedCompanyId || isLoadingSites}
              >
                <option value="">
                  {!selectedCompanyId
                    ? 'Select a company first'
                    : isLoadingSites
                    ? 'Loading registered project sites...'
                    : companySites.length === 0
                    ? 'No registered project site'
                    : 'Select a registered project site'}
                </option>
                {location && !companySites.some((site) => site.name === location) && (
                  <option value={location}>{location}</option>
                )}
                {companySites.map((site) => (
                  <option key={site.id || `${site.name}-${site.address}`} value={site.name}>
                    {site.address && site.address !== site.name ? `${site.name} — ${site.address}` : site.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div className="col-12">
              <label className="form-label fw-semibold">
                Notes{' '}
                <span className="text-muted fw-normal" style={{ fontSize: '0.8em' }}>
                  (optional)
                </span>
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Agenda, objectives, or additional details..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="offcanvas-footer border-top p-3">
        <div className="d-flex justify-content-end gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            data-bs-dismiss="offcanvas"
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                />
                Saving...
              </>
            ) : (
              editData ? `Update ${scheduleTypeLabel}` : `Save ${scheduleTypeLabel}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddGeneralScheduleOffcanvas;
